/**
 * AFIP (Argentina) invoice QR payload builder — RG 4892/2020.
 *
 * Pure, framework-agnostic module: no Vue imports, no side effects.
 * Ported from cartadigital-pos (app/domain/invoicing/ar-afip/qr-payload.ts);
 * keep both copies logically identical.
 *
 * The payload is an explicit allowlist of the fields defined by the spec;
 * anything else stored in the invoice data (impIVA, caeVto, impNeto, ...)
 * must NOT leak into the QR.
 */

export interface IAfipQrPayload {
  ver: number;
  fecha: string;
  cuit: number;
  ptoVta: number;
  tipoCmp: number;
  nroCmp: number;
  importe: number;
  moneda: string;
  ctz: number;
  tipoDocRec: number;
  nroDocRec: number;
  tipoCodAut: string;
  codAut: number;
}

function toNumber(value: unknown, fallback = 0): number {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? Number(value)
      : NaN;
  return Number.isFinite(n) ? n : fallback;
}

function toBase64Utf8(input: string): string {
  // Prefer browser btoa; fallback to Node Buffer when available.
  if (typeof globalThis.btoa === 'function') {
    try {
      return globalThis.btoa(input);
    } catch {
      // Unicode-safe fallback for btoa
      const encoded = encodeURIComponent(input).replace(
        /%([0-9A-F]{2})/g,
        (_m, p1: string) => String.fromCharCode(parseInt(p1, 16))
      );
      return globalThis.btoa(encoded);
    }
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(input, 'utf8').toString('base64');
  }

  throw new Error('Base64 no disponible en este entorno');
}

/**
 * Build the RG 4892 QR payload from a stored invoice data record.
 */
export function buildAfipQrPayload(
  data: Record<string, unknown>
): IAfipQrPayload {
  return {
    ver: toNumber(data.ver, 1),
    fecha: typeof data.fecha === 'string' ? data.fecha : '',
    cuit: toNumber(data.cuit, 0),
    ptoVta: toNumber(data.ptoVta, 0),
    tipoCmp: toNumber(data.tipoCmp, 0),
    nroCmp: toNumber(data.nroCmp, 0),
    importe: toNumber(data.importe, 0),
    moneda: typeof data.moneda === 'string' ? data.moneda : 'PES',
    ctz: toNumber(data.ctz, 1),
    tipoDocRec: toNumber(data.tipoDocRec, 0),
    nroDocRec: toNumber(data.nroDocRec, 0),
    tipoCodAut: typeof data.tipoCodAut === 'string' ? data.tipoCodAut : 'E',
    codAut: toNumber(data.codAut, 0),
  };
}

/**
 * Build the scannable AFIP QR URL for a stored invoice data record.
 */
export function buildAfipQrUrl(data: Record<string, unknown>): string {
  const payloadB64 = toBase64Utf8(JSON.stringify(buildAfipQrPayload(data)));
  return `https://www.afip.gob.ar/fe/qr/?p=${payloadB64}`;
}
